import { ApiError } from '@axiom/ui';

/*
  The three the api calls out that a reader can do something about. All arrive as a plain `ApiError`:
  `createErrorFromResponse` has a case for none of them, so nothing types them for us.
*/
const CONFLICT_STATUS = 409;
const LOCKED_STATUS = 423;
/*
  Apart from the other two in what it means for the conversation: they are moments, and this is the
  end of it. Every following send fails the same way, so there is nothing to wait for or refresh.
*/
const CONTEXT_FULL_STATUS = 422;

/*
  The rest of what the chat endpoints declare, and the reason they are grouped: a missing chat, a
  request the server would not take and a read it could not finish are all things a reader can do
  nothing about. Retrying is not the answer, so they are worded and colored differently.
*/
const STOPPER_STATUSES = new Set([400, 404, 500]);

/**
 * ChatErrorUtil - I say which of the failures the api declares this one is. ONLY concerned with
 * reading a status off an error - no wording, no state.
 *
 * The api declares six: 409 and 423 pass, 422 ends the conversation, and 400, 404 and 500 are
 * nothing the reader can act on. **A single status that `@axiom/ui` gives a class of its own is
 * not worth a method here** - `error instanceof NotFoundError` says it at the call site.
 */
export const ChatErrorUtil = {
  /** The conversation moved on under us - the server's copy is newer than the one we sent against. */
  isStale({ error }: { error: unknown }) {
    return (
      error instanceof ApiError && error.getStatusCode() === CONFLICT_STATUS
    );
  },

  /**
   * Another process holds the conversation. Declared on renaming, hiding, uploading and sending, so
   * every write here can meet it.
   */
  isLocked({ error }: { error: unknown }) {
    return error instanceof ApiError && error.getStatusCode() === LOCKED_STATUS;
  },

  /**
   * The conversation has no room left. **Not a moment like the other two** - every following send
   * meets the same wall, so the only way on is a new chat.
   */
  isContextFull({ error }: { error: unknown }) {
    return (
      error instanceof ApiError && error.getStatusCode() === CONTEXT_FULL_STATUS
    );
  },

  /** Nothing the reader can act on now - see `STOPPER_STATUSES`. */
  isStopper({ error }: { error: unknown }) {
    return (
      error instanceof ApiError && STOPPER_STATUSES.has(error.getStatusCode())
    );
  },
};
